/**
 * "Recently compared" for the Data Compare setup dialog — Task B4 (Soporte3,
 * F07), now a thin adapter over the shared `RecentlyComparedPanel` that also
 * backs Schema Compare. The panel owns the layout, the "show N earlier"
 * disclosure, the search box and the collapse-to-link behaviour; round-3
 * review R3-10 asked for that interaction to be identical in both dialogs, so
 * it lives in one place rather than in two copies that can drift.
 *
 * This file's only job is Data-Compare-specific: read the recents store,
 * describe each entry as a `RecentComparedItem` (endpoint detail =
 * `database · schema.table`, badge = key/value mapping counts, since that is
 * the configuration Data Compare users actually want back — see the F07
 * finding, "1 key column, 9/9 value columns"), and fire this feature's
 * analytics events.
 *
 * Dead-entry behaviour (deliberate, see task-B4-report.md): an entry whose
 * connection no longer exists stays visible with an inline "(connection
 * unavailable)" warning rather than being hidden or disabled — selecting it
 * still opens a configured tab, which surfaces the app's existing
 * connection-lost banner/retry flow rather than a bespoke dead-end here.
 */
import { useMemo } from 'react';
import { useConnectionStore } from '@/features/connections/store/connectionStore';
import { useAnalytics } from '@/core/analytics/useAnalytics';
import {
  RecentlyComparedLinkButton,
  RecentlyComparedPanel,
  type RecentComparedItem,
  type RecentEntryPosition,
} from '@/features/workspace/common/components/RecentlyComparedPanel';
import {
  useRecentDataCompareStore,
  type RecentDataCompareEntry,
} from '../store/recentDataCompareStore';
import type { DataCompareConnectionEndpoint } from '../../core/types/workspaceTypes';

const ID_PREFIX = 'recent-data-compare';

type RecentlyComparedSectionProps = {
  onSelect: (entry: RecentDataCompareEntry) => void;
};

function formatTableRef(e: DataCompareConnectionEndpoint): string {
  const schemaPart = e.schema ? `${e.schema}.` : '';
  return `${e.database} · ${schemaPart}${e.table}`;
}

function toItem(entry: RecentDataCompareEntry, knownConnectionIds: Set<string>): RecentComparedItem {
  const includedValueCount = entry.valueMappings.filter((v) => v.included).length;

  return {
    id: entry.id,
    sourceLabel: entry.sourceConnectionName || entry.sourceServerHost || 'Source',
    sourceDetail: formatTableRef(entry.source),
    targetLabel: entry.targetConnectionName || entry.targetServerHost || 'Target',
    targetDetail: formatTableRef(entry.target),
    badgeLabel: `${entry.keyMappings.length} key • ${includedValueCount}/${entry.valueMappings.length} value`,
    comparedAt: entry.comparedAt,
    connectionExists:
      knownConnectionIds.has(entry.source.connectionId) &&
      knownConnectionIds.has(entry.target.connectionId),
    searchText: [
      entry.sourceConnectionName,
      entry.sourceServerHost,
      entry.source.database,
      entry.source.table,
      entry.targetConnectionName,
      entry.targetServerHost,
      entry.target.database,
      entry.target.table,
    ]
      .filter(Boolean)
      .join(' ')
      .toLowerCase(),
  };
}

/**
 * The collapsed affordance, rendered in the dialog header. Renders nothing
 * when there is no history to offer.
 */
export function RecentlyComparedLink({ open, onToggle }: { open: boolean; onToggle: () => void }) {
  const hasEntries = useRecentDataCompareStore((s) => s.entries.length > 0);
  const { track } = useAnalytics();

  if (!hasEntries) return null;

  return (
    <RecentlyComparedLinkButton
      open={open}
      testId={`${ID_PREFIX}-link`}
      onToggle={() => {
        track('feature:data_compare_recent_toggle', { enabled: !open });
        onToggle();
      }}
    />
  );
}

export function RecentlyComparedSection({ onSelect }: RecentlyComparedSectionProps) {
  const entries = useRecentDataCompareStore((s) => s.entries);
  const remove = useRecentDataCompareStore((s) => s.remove);
  const connections = useConnectionStore((s) => s.connections);
  const { track } = useAnalytics();

  const items = useMemo(() => {
    const knownConnectionIds = new Set(connections.map((c) => c.id));
    return entries.map((entry) => toItem(entry, knownConnectionIds));
  }, [entries, connections]);

  const handleSelect = (id: string, position: RecentEntryPosition) => {
    const entry = entries.find((e) => e.id === id);
    if (!entry) return;
    track('feature:data_compare_recent_select', { position });
    onSelect(entry);
  };

  const handleRemove = (id: string) => {
    track('feature:data_compare_recent_delete');
    remove(id);
  };

  return (
    <RecentlyComparedPanel
      items={items}
      sectionTestId="recently-compared-data-section"
      idPrefix={ID_PREFIX}
      searchPlaceholder="Search by connection or table…"
      onSelect={handleSelect}
      onRemove={handleRemove}
    />
  );
}
